import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./Button";
import { Select } from "./Select";

// 列表底下的分頁列:上一頁 / 下一頁,外加「每頁幾筆」。
//
// 業主 2026-10-13:「報價單 共 23 筆,目前顯示前 20 筆。你沒給我下一頁 / 上一頁,
// 也沒給我選項說一頁顯示幾筆」。之前只寫了一句「目前顯示前 N 筆」,第 21 張起
// 根本看不到。
//
// 檔位上限 100 是後端的 `size` 上限(`le=100`),多一檔送上去只會被打回 422。

export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const DEFAULT_SIZE = PAGE_SIZE_OPTIONS[0];
const SIZE_OPTIONS = PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }));

function readSize(storageKey: string): number {
  // 隱私模式或封鎖網站資料時 localStorage 會丟例外,讀不到就用預設的那一檔。
  try {
    const raw = Number(window.localStorage.getItem(storageKey));
    return (PAGE_SIZE_OPTIONS as readonly number[]).includes(raw) ? raw : DEFAULT_SIZE;
  } catch {
    return DEFAULT_SIZE;
  }
}

/** 每頁幾筆,記在這台瀏覽器上(每張列表一個 key)。 */
export function usePageSize(storageKey: string): [number, (size: number) => void] {
  const [size, setSize] = useState(() => readSize(storageKey));
  const update = (next: number) => {
    setSize(next);
    try {
      window.localStorage.setItem(storageKey, String(next));
    } catch {
      /* 存不了就只在這一次有效 */
    }
  };
  return [size, update];
}

/** 總共有幾頁。一筆都沒有時算 1 頁,「第 1 / 0 頁」看起來像壞掉。 */
export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}

export interface PaginationProps {
  total: number;
  /** 從 1 開始。 */
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  /** 換檔位。呼叫端要自己把頁碼退回 1 —— 第 3 頁在每頁 100 筆時可能不存在。 */
  onSizeChange: (size: number) => void;
}

export function Pagination({ total, page, size, onPageChange, onSizeChange }: PaginationProps) {
  // 一頁就裝得下、而且已經是最小那一檔:整列不畫,沒有東西可翻也沒有東西可選。
  // 選了 50 之後的 23 筆**還要畫** —— 不然沒有地方改回 20。
  if (total <= DEFAULT_SIZE && size === DEFAULT_SIZE) return null;

  const pages = pageCount(total, size);
  const first = total === 0 ? 0 : (page - 1) * size + 1;
  const last = Math.min(page * size, total);

  return (
    <nav aria-label="分頁" className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[var(--text-2)]">
      <span className="tabular">{`共 ${total} 筆,第 ${first}–${last} 筆`}</span>
      <span className="flex items-center gap-2">
        <Button
          size="sm"
          icon={<ChevronLeft aria-hidden="true" size={16} />}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          上一頁
        </Button>
        <span className="tabular whitespace-nowrap">{`第 ${page} / ${pages} 頁`}</span>
        <Button size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
          下一頁
          <ChevronRight aria-hidden="true" size={16} />
        </Button>
      </span>
      <span className="flex items-center gap-1.5">
        每頁
        <span className="w-20">
          <Select
            aria-label="每頁筆數"
            value={String(size)}
            onChange={(event) => onSizeChange(Number(event.target.value))}
            options={SIZE_OPTIONS}
          />
        </span>
        筆
      </span>
    </nav>
  );
}
